import { L } from '@salesport/logger';

import { config } from '../config';
import { Gtag } from '../types';

function isValidConsentStatus(value: unknown): value is Gtag.ConsentStatus {
  return typeof value === 'string' && ['granted', 'denied'].includes(value);
}

function isValidConsentPreferences(
  value: unknown,
): value is Gtag.ConsentPreferences {
  if (!value || typeof value !== 'object') return false;

  const requiredKeys: Array<keyof Gtag.ConsentPreferences> = [
    'ad_user_data',
    'ad_personalization',
    'ad_storage',
    'analytics_storage',
  ];

  return requiredKeys.every(
    (key) =>
      key in value &&
      isValidConsentStatus((value as Record<string, unknown>)[key]),
  );
}

export function getConsentCookie(): Gtag.ConsentPreferences | null {
  try {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${config.cookieName}=`));

    if (!cookie) return null;

    const value = cookie.split('=')[1];
    const parsedValue = JSON.parse(decodeURIComponent(value));

    return isValidConsentPreferences(parsedValue) ? parsedValue : null;
  } catch (error) {
    L.instance.error(error, 'Error reading consent cookie');
    return null;
  }
}

export function setConsentCookie(value: Gtag.ConsentPreferences): void {
  // Validate the input value at runtime as well
  if (!isValidConsentPreferences(value)) {
    throw new Error('Invalid consent preferences provided');
  }

  // Set cookie expiration based on config
  const expirationDate = new Date();
  expirationDate.setDate(
    expirationDate.getDate() + config.cookieExpirationDays,
  );

  const cookieValue = encodeURIComponent(JSON.stringify(value));
  document.cookie = `${config.cookieName}=${cookieValue}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
}
